import {
    CONVERSATIONAL_MM_AGENT,
    toMastraResourceId,
} from '@fruitful/llm-service-shared';
import { AnalyticsUserEvent } from '@fruitful/types';
import { router, useLocalSearchParams } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { FullScreenSpinner } from 'smoothie';

import { StorageKeys } from '../../../common/constants';
import { LLM_SERVICE_BASE_URL } from '../../../config/config';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import {
    MONEY_GOAL_PARAM,
    OnboardingChatContainer,
} from '../../../llm-service/components/OnboardingChatContainer';
import { OnboardingChatShell } from '../../../llm-service/components/shared-chat/OnboardingChatShell';
import { useMastraChat } from '../../../llm-service/hooks/useMastraChat';
import { useTrackThreadMetadata } from '../../../llm-service/hooks/useTrackThreadMetadata';
import {
    isBuildingPlanAtom,
    isChatFadingOutAtom,
    isChatInputLockedAtom,
    isPlanReadyAtom,
} from '../../../llm-service/state/OnboardingChatAtoms';
import {
    captureException,
    track,
    trackGrowthMarketingEvent,
} from '../../../logger/Logger';
import { useUser } from '../../../user/providers/UserProvider';
import { useVisitorId } from '../../../user/providers/VisitorProvider';

const V2_STREAM_URL = `${LLM_SERVICE_BASE_URL}/member/cmm/v2/stream`;

/**
 * How long a CMM session can sit idle before we drop it on the next fresh mount
 * (hard refresh, or navigating away and back), same as hitting "Start over".
 * 24 hours — a session older than a day is treated as abandoned.
 */
const CMM_SESSION_INACTIVITY_TIMEOUT_MS = 24 * 60 * 60 * 1000;

const ChatPage = () => {
    const { loading: isLoadingUser } = useUser();
    const { trackViewOnce } = useViewEvents();
    const hasLoggedSessionTypeRef = useRef(false);
    const isChatInputLocked = useAtomValue(isChatInputLockedAtom);
    const setIsBuildingPlan = useSetAtom(isBuildingPlanAtom);
    const setIsPlanReady = useSetAtom(isPlanReadyAtom);
    const setIsChatFadingOut = useSetAtom(isChatFadingOutAtom);
    const setIsChatInputLocked = useSetAtom(isChatInputLockedAtom);
    const threadStorageKey = StorageKeys.CONVERSATIONAL_MM_THREAD_ID;
    const visitorId = useVisitorId();
    const params = useLocalSearchParams();
    const resourceId = toMastraResourceId(
        CONVERSATIONAL_MM_AGENT.id,
        visitorId,
    );

    useEffect(() => {
        track(AnalyticsUserEvent.CMM_SESSION_NEW, {
            cmmStreamVersion: 'v2',
            streamUrl: V2_STREAM_URL,
        });
        void fetch(`${LLM_SERVICE_BASE_URL}/health-check`).catch((err) => {
            captureException(err, {
                context: 'LLM service warm-up ping failed',
            });
        });
    }, []);

    // When an idle session is dropped, reset the plan-transition atoms. They're
    // module-level globals that intentionally survive remounts, so without this
    // a finished session's "putting your plan together" message bleeds onto the
    // fresh welcome screen. Mirrors the "Start over" reset.
    const handleSessionExpired = useCallback(() => {
        setIsBuildingPlan(false);
        setIsPlanReady(false);
        setIsChatFadingOut(false);
        setIsChatInputLocked(false);
    }, [
        setIsBuildingPlan,
        setIsPlanReady,
        setIsChatFadingOut,
        setIsChatInputLocked,
    ]);

    const {
        messages,
        threadMetadata,
        isStreaming,
        showTypingIndicator,
        isRestoringHistory,
        sendMessage,
        sendMessageWithAgentPayload,
        classifyCustomSavingsGoal,
        setBranch,
        sendSilentMessage,
        stopStreaming,
        clearMessages,
        addCollectedModule,
    } = useMastraChat({
        agent: CONVERSATIONAL_MM_AGENT,
        resourceId,
        persistThreadStorageKey: threadStorageKey,
        restoreThreadMessages: true,
        // Clear an idle CMM session (24h+) on refresh / navigate-away-and-back,
        // like "Start over".
        sessionInactivityTimeoutMs: CMM_SESSION_INACTIVITY_TIMEOUT_MS,
        onSessionExpired: handleSessionExpired,
        streamUrl: V2_STREAM_URL,
    });

    const handleResetThread = useCallback(() => {
        clearMessages();
        // Drop a `money-goal` deep link on "Start over" so the member gets the
        // normal welcome menu instead of auto-answering the same goal again.
        const { [MONEY_GOAL_PARAM]: _omitMoneyGoal, ...restParams } = params;
        router.replace({
            pathname: '/sign-up/get-started',
            params: restParams,
        });
    }, [clearMessages, params]);

    // Intentionally a no-op: we do NOT strip the `money-goal` param from the URL
    // after the deep link is consumed.
    //
    // Stripping it (via router.navigate/replace to drop the key) remounts the
    // screen, which kicks off a history-restore that races the just-fired
    // set-branch commit. The thread isn't in the DB yet, so the restore lookup
    // 404s and the chat hook mints a brand-new threadId — orphaning the thread
    // set-branch created. Every later turn then runs against an uncreated
    // thread (hasThread: false), so state never persists and the plan re-asks
    // the same questions forever. This ONLY happens on the deep-link path
    // because it's the only path that strips/remounts; the normal welcome menu
    // never does, which is why it works fine.
    //
    // Leaving the param is safe: the auto-answer effect is guarded by
    // `messages.length === 0` + `isRestoringHistory`, so a refresh that
    // restores the real thread skips the auto-answer rather than re-answering.
    // "Start over" (handleResetThread) still drops the param via router.replace
    // to intentionally force a fresh menu.
    const handleDeepLinkConsumed = useCallback(() => {
        // No-op — keep the param; see comment above.
    }, []);

    useTrackThreadMetadata(threadMetadata);

    useEffect(() => {
        if (
            isLoadingUser ||
            isRestoringHistory ||
            hasLoggedSessionTypeRef.current
        ) {
            return;
        }

        hasLoggedSessionTypeRef.current = true;
        trackViewOnce('SignUp/GetStarted-CMM-Chat');

        if (messages.length > 0) {
            track(AnalyticsUserEvent.CMM_SESSION_RESTORED, {
                messagesCount: messages.length,
                visitorId,
                resourceId,
            });
        } else {
            trackGrowthMarketingEvent(AnalyticsUserEvent.SIGNUP_FUNNEL_INIT, {
                visitorId,
            });
            track(AnalyticsUserEvent.CMM_SESSION_NEW, {
                visitorId,
                resourceId,
            });
        }
    }, [
        isLoadingUser,
        isRestoringHistory,
        messages.length,
        visitorId,
        resourceId,
    ]);

    if (isLoadingUser) {
        return <FullScreenSpinner />;
    }

    return (
        <OnboardingChatShell>
            <OnboardingChatContainer
                messages={messages}
                isStreaming={isStreaming}
                isRestoringHistory={isRestoringHistory}
                showTypingIndicator={showTypingIndicator}
                disabled={isChatInputLocked}
                threadMetadata={threadMetadata}
                onSendMessage={sendMessage}
                onSendMessageWithAgentPayload={sendMessageWithAgentPayload}
                onClassifyCustomSavingsGoal={classifyCustomSavingsGoal}
                onSetBranch={setBranch}
                onSendSilentMessage={sendSilentMessage}
                onStopStreaming={stopStreaming}
                onAddCollectedModule={addCollectedModule}
                onResetThread={handleResetThread}
                onDeepLinkConsumed={handleDeepLinkConsumed}
            />
        </OnboardingChatShell>
    );
};

export default ChatPage;
